import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  exchangeId?: string;
}

export interface ChatRequest {
  query: string;
  agentName: string;
  sessionId?: string;
  /** When true, portal chat starts a fresh harness session. */
  forceNewSession?: boolean;
  userId?: string;
  /** Pin chat to a draft/sandbox/published version (sent as agent-version). */
  agentVersion?: string;
  /** Override model for this turn (sent as model). */
  model?: string;
  /** Effort level for this turn: low | medium | high. */
  effort?: 'low' | 'medium' | 'high';
  attachments?: AttachmentRef[];
  /** Portal chat metadata (source, prompt_context, …). */
  metadata?: Record<string, unknown>;
  consumerUserId?: string;
  consumerOrgId?: string;
  parameters?: Record<string, string | string[]>;
  extraHeaders?: Record<string, string>;
}

export interface ChatResponse {
  response: string;
  sessionId: string;
  exchangeId: string;
  isAsync?: boolean;
  conversationId?: string;
  agentVersion?: string;
  versionEnvironment?: string;
  isLatest?: boolean;
  /** Present on async agents (`response_mode: async`) while the processor runs. */
  status?: 'completed' | 'processing' | 'failed' | string;
  media?: { url?: string; mime?: string; width?: number; height?: number }[];
  mediaJob?: { jobId?: string; status?: string };
  /** Present when the turn used Auto. No prices. */
  modelSelection?: {
    productId?: string;
    policyId?: string;
    reasonCode?: string;
  };
}

export interface AttachmentRef {
  type: 'image' | 'document';
  s3Key: string;
  fileName: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
}

export interface ConversationResult {
  id: string;
  status: 'completed' | 'processing' | 'failed';
  response?: string;
}

export interface VoiceTtsRequest {
  text: string;
  voice?: string;
  model?: string;
}

export interface VoiceTtsResponse {
  audio: string;
  contentType?: string;
  'content-type'?: string;
  voice?: string;
  model?: string;
}

export interface VoiceSttRequest {
  audio: string;
  'content-type'?: string;
}

export interface VoiceSttResponse {
  text: string;
}

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    sendMessage: builder.mutation<ChatResponse, ChatRequest>({
      query: (body) => ({
        url: '/portal/chat',
        method: 'POST',
        headers: body.extraHeaders,
        body: {
          query: body.query,
          'agent-name': body.agentName,
          'session-id': body.sessionId,
          ...(body.forceNewSession ? { 'force-new-session': true } : {}),
          ...(body.agentVersion ? { 'agent-version': body.agentVersion } : {}),
          ...(body.model ? { model: body.model } : {}),
          ...(body.effort ? { effort: body.effort } : {}),
          attachments: body.attachments,
          metadata: body.metadata,
          ...(body.consumerUserId
            ? { 'consumer-user-id': body.consumerUserId }
            : {}),
          ...(body.consumerOrgId
            ? { 'consumer-org-id': body.consumerOrgId }
            : {}),
          ...(body.parameters && Object.keys(body.parameters).length > 0
            ? { parameters: body.parameters }
            : {}),
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<ChatResponse>(response),
    }),

    getUploadUrl: builder.mutation<UploadUrlResponse, { fileName: string; contentType: string }>({
      query: (body) => ({
        url: '/files/upload-url',
        method: 'POST',
        body: {
          'file-name': body.fileName,
          'content-type': body.contentType,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<UploadUrlResponse>(response),
    }),

    getConversation: builder.query<ConversationResult, string>({
      query: (id) => `/conversations/${id}`,
      transformResponse: (response: unknown) => normalizeKeys<ConversationResult>(response),
    }),

    textToSpeech: builder.mutation<VoiceTtsResponse, VoiceTtsRequest>({
      query: (body) => ({
        url: '/portal/voice/tts',
        method: 'POST',
        body,
      }),
    }),

    speechToText: builder.mutation<VoiceSttResponse, VoiceSttRequest>({
      query: (body) => ({
        url: '/portal/voice/stt',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetUploadUrlMutation,
  useGetConversationQuery,
  useLazyGetConversationQuery,
  useTextToSpeechMutation,
  useSpeechToTextMutation,
} = chatApi;
